<template>
  <div class="search-container">
    <el-form 
      class="search-form" 
      :model="searchForm" 
      ref="searchForm" 
      :inline="true" 
      label-width="93px"
      label-position="right"
    >
      <!-- 默认显示的筛选项 -->
      <el-form-item
        v-for="config in visibleSearchConfigs"
        :key="`${config.field}_${config.options ? config.options.length : 0}`"
        :prop="config.field"
        :label="config.label | withColon"
      >
          <!-- 输入框类型 -->
          <el-input
            v-if="config.type === 'input'"
            v-model="searchForm[config.field]"
            :placeholder="config.placeholder || config.label"
            clearable
            class="search-input"
          >
          </el-input>

          <!-- 下拉单选类型 -->
          <el-select
            v-else-if="config.type === 'select'"
            v-model="searchForm[config.field]"
            :placeholder="config.placeholder || config.label"
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="option in config.options || []"
              :key="option.value"
              :label="option.label || option.text"
              :value="option.value"
            ></el-option>
          </el-select>

          <!-- 下拉多选类型 -->
          <el-select
            v-else-if="config.type === 'multiple-select'"
            v-model="searchForm[config.field]"
            :placeholder="config.placeholder || config.label"
            multiple
            clearable
            filterable
            class="search-select"
          >
            <el-option
              v-for="option in config.options || []"
              :key="option.value"
              :label="option.label || option.text"
              :value="option.value"
            ></el-option>
          </el-select>
        </el-form-item>

      <!-- 展开后显示的筛选项 -->
      <template v-if="!isCollapsed">
        <el-form-item
          v-for="config in hiddenSearchConfigs"
          :key="`${config.field}_${config.options ? config.options.length : 0}`"
          :prop="config.field"
          :label="config.label | withColon"
        >
            <!-- 输入框类型 -->
            <el-input
              v-if="config.type === 'input'"
              v-model="searchForm[config.field]"
              :placeholder="config.placeholder || config.label"
              clearable
              class="search-input"
            >
            </el-input>

            <!-- 下拉单选类型 -->
            <el-select
              v-else-if="config.type === 'select'"
              v-model="searchForm[config.field]"
              :placeholder="config.placeholder || config.label"
              clearable
              filterable
              class="search-select"
            >
              <el-option
                v-for="option in config.options || []"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              ></el-option>
            </el-select>

            <!-- 下拉多选类型 -->
            <el-select
              v-else-if="config.type === 'multiple-select'"
              v-model="searchForm[config.field]"
              :placeholder="config.placeholder || config.label"
              multiple
              clearable
              filterable
              class="search-select"
            >
              <el-option
                v-for="option in config.options || []"
                :key="option.value"
                :label="option.label || option.text"
                :value="option.value"
              ></el-option>
            </el-select>
          </el-form-item>
        </template>

      <!-- 按钮组 -->
      <el-form-item class="search-buttons">
        <el-button type="primary" @click="handleSearchClick">
          {{ $t('text.search') }}
        </el-button>
        <el-button @click="handleResetClick">
          {{ $t('text.reset') }}
        </el-button>
        <el-button 
          @click="toggleCollapse" 
          type="text" 
          v-if="showCollapseButton && hiddenSearchConfigs.length"
        >
          <template v-if="isCollapsed">
            {{ $t('text.expand') }} <i class="el-icon-arrow-down"></i>
          </template>
          <template v-else>
            {{ $t('text.collapse') }} <i class="el-icon-arrow-up"></i>
          </template>
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
export default {
  name: 'SearchListPane',
  
  props: {
    // 搜索配置数组
    searchConfigs: {
      type: Array,
      required: true,
      default: () => []
    },
    // 搜索表单数据
    searchFormData: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // 默认显示的搜索项数量
    defaultVisibleCount: {
      type: Number,
      default: 3
    },
    // 是否显示展开/收起按钮
    showCollapseButton: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      isCollapsed: true,
      searchForm: { ...this.searchFormData }
    };
  },

  computed: {
    // 可见的搜索配置
    visibleSearchConfigs () {
      return this.searchConfigs.slice(0, this.defaultVisibleCount);
    },
    // 隐藏的搜索配置
    hiddenSearchConfigs () {
      return this.searchConfigs.slice(this.defaultVisibleCount);
    }
  },

  watch: {
    searchFormData: {
      deep: true,
      handler (newVal) {
        // 只有当值确实发生变化时才更新本地searchForm
        if (JSON.stringify(this.searchForm) !== JSON.stringify(newVal)) {
          this.searchForm = { ...newVal };
        }
      }
    },
    searchConfigs: {
      deep: true,
      handler () {
        // 当searchConfigs发生变化时，重新渲染搜索表单
        this.$nextTick(() => {
          // 强制重新渲染组件以更新选项
          this.$forceUpdate();
        });
      }
    }
  },

  methods: {
    toggleCollapse () {
      this.isCollapsed = !this.isCollapsed;
    },
    
    // 重置表单
    resetForm () {
      this.$refs.searchForm.resetFields();
    },
    
    // 获取搜索参数
    getSearchParams () {
      return { ...this.searchForm };
    },
    
    // 处理搜索点击
    handleSearchClick () {
      this.$emit('search', { ...this.searchForm });
    },
    
    // 处理重置点击
    handleResetClick () {
      this.resetForm();
      this.$emit('reset', { ...this.searchForm });
    }
  },

  filters: {
    withColon (value) {
      return value ? value + ':' : '';
    }
  }
};
</script>

<style lang="scss">
@import "./searchListPane.scss";
</style>


// WEBPACK FOOTER //
// src/components/common/search/SearchListPane.vue